"use client";

import { useSignals } from "@preact/signals-react/runtime";
import { useCallback, useEffect, useId, useRef } from "react";

import {
  createManagedHistoryEntry,
  getCurrentHref,
  getCurrentManagedHistoryEntry,
  type HistoryManagerParams,
  historyManagerSignal,
  type ManagedHistoryEntry,
  pushManagedHistoryEntry,
  replaceHistoryUrl,
  replaceManagedHistoryEntry,
  syncHistoryManagerState,
} from "@/utils/history-manager";

export type HistoryManagerBackResult = "close" | "handled" | void;

type UseHistoryManagerOptions = {
  enabled?: boolean;
  instanceId?: string;
  kind: string;
  name: string;
  onBrowserBack?: (entry: ManagedHistoryEntry) => HistoryManagerBackResult;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  params?: HistoryManagerParams;
  replace?: boolean;
};

const isSameEntryOwner = ({
  entry,
  instanceId,
  kind,
  name,
}: {
  entry: ManagedHistoryEntry | null;
  instanceId: string;
  kind: string;
  name: string;
}) => {
  return (
    entry?.kind === kind &&
    entry.name === name &&
    entry.instanceId === instanceId
  );
};

const findOwnedEntry = ({
  entry,
  instanceId,
  kind,
  name,
}: {
  entry: ManagedHistoryEntry | null;
  instanceId: string;
  kind: string;
  name: string;
}) => {
  let currentEntry = entry;

  while (currentEntry) {
    if (
      isSameEntryOwner({
        entry: currentEntry,
        instanceId,
        kind,
        name,
      })
    ) {
      return currentEntry;
    }

    currentEntry = currentEntry.parent ?? null;
  }

  return null;
};

export const useHistoryManagerRuntime = () => {
  useEffect(() => {
    syncHistoryManagerState();

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    const syncTimeouts = new Set<number>();

    const scheduleHistorySync = (source: "push" | "replace") => {
      const timeout = window.setTimeout(() => {
        syncTimeouts.delete(timeout);
        syncHistoryManagerState(source);
      }, 0);

      syncTimeouts.add(timeout);
    };

    const patchedPushState: History["pushState"] = (...args) => {
      const result = originalPushState.apply(window.history, args);
      scheduleHistorySync("push");
      return result;
    };
    const patchedReplaceState: History["replaceState"] = (...args) => {
      const result = originalReplaceState.apply(window.history, args);
      scheduleHistorySync("replace");
      return result;
    };
    const handlePopState = () => {
      syncHistoryManagerState("popstate");
    };

    window.history.pushState = patchedPushState;
    window.history.replaceState = patchedReplaceState;
    window.addEventListener("popstate", handlePopState);

    return () => {
      if (window.history.pushState === patchedPushState) {
        window.history.pushState = originalPushState;
      }

      if (window.history.replaceState === patchedReplaceState) {
        window.history.replaceState = originalReplaceState;
      }

      syncTimeouts.forEach((timeout) => {
        window.clearTimeout(timeout);
      });
      syncTimeouts.clear();

      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
};

export const useHistoryManager = ({
  enabled = true,
  instanceId: instanceIdProp,
  kind,
  name,
  onBrowserBack,
  onOpenChange,
  open,
  params,
  replace = false,
}: UseHistoryManagerOptions) => {
  useSignals();

  const reactInstanceId = useId();
  const instanceId = instanceIdProp ?? reactInstanceId;
  const ownedEntryRef = useRef<ManagedHistoryEntry | null>(null);
  const pendingCloseEntryIdRef = useRef<string | null>(null);
  const onBrowserBackRef = useRef(onBrowserBack);
  const onOpenChangeRef = useRef(onOpenChange);

  onBrowserBackRef.current = onBrowserBack;
  onOpenChangeRef.current = onOpenChange;

  const { activeEntry, revision, source } = historyManagerSignal.value;

  const openHistoryEntry = useCallback(() => {
    const entry = createManagedHistoryEntry({
      instanceId,
      kind,
      name,
      params,
    });

    if (!entry) {
      pendingCloseEntryIdRef.current = null;
      onOpenChangeRef.current(true);
      return;
    }

    pendingCloseEntryIdRef.current = null;
    ownedEntryRef.current = entry;
    const currentEntry = getCurrentManagedHistoryEntry();

    if (replace && currentEntry) {
      replaceManagedHistoryEntry(entry);
    } else {
      pushManagedHistoryEntry(entry);
    }

    onOpenChangeRef.current(true);
  }, [instanceId, kind, name, params, replace]);

  const closeHistoryEntry = useCallback(() => {
    const currentEntry = getCurrentManagedHistoryEntry();
    const ownsCurrentEntry = isSameEntryOwner({
      entry: currentEntry,
      instanceId,
      kind,
      name,
    });

    pendingCloseEntryIdRef.current = ownsCurrentEntry
      ? (currentEntry?.entryId ?? null)
      : null;
    ownedEntryRef.current = null;
    onOpenChangeRef.current(false);

    if (ownsCurrentEntry) {
      window.history.back();
    }
  }, [instanceId, kind, name]);

  const dismissHistoryEntry = useCallback(() => {
    const currentEntry = getCurrentManagedHistoryEntry();
    const ownsCurrentEntry = isSameEntryOwner({
      entry: currentEntry,
      instanceId,
      kind,
      name,
    });

    pendingCloseEntryIdRef.current = null;
    ownedEntryRef.current = null;
    onOpenChangeRef.current(false);

    if (ownsCurrentEntry && currentEntry) {
      replaceHistoryUrl(getCurrentHref() ?? currentEntry.href);
      syncHistoryManagerState("replace");
    }
  }, [instanceId, kind, name]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!enabled) {
        onOpenChangeRef.current(nextOpen);
        return;
      }

      if (nextOpen) {
        openHistoryEntry();
        return;
      }

      closeHistoryEntry();
    },
    [closeHistoryEntry, enabled, openHistoryEntry],
  );

  useEffect(() => {
    if (!enabled) {
      ownedEntryRef.current = null;
      pendingCloseEntryIdRef.current = null;
      return;
    }

    const ownedActiveEntry = findOwnedEntry({
      entry: activeEntry,
      instanceId,
      kind,
      name,
    });

    if (ownedActiveEntry) {
      if (pendingCloseEntryIdRef.current === ownedActiveEntry.entryId) {
        return;
      }

      const hadOwnedEntry = ownedEntryRef.current;
      ownedEntryRef.current = ownedActiveEntry;

      if (!open && (source === "popstate" || !hadOwnedEntry)) {
        onOpenChangeRef.current(true);
      }

      return;
    }

    pendingCloseEntryIdRef.current = null;
    const ownedEntry = ownedEntryRef.current;

    if (!open) {
      return;
    }

    if (!ownedEntry && source !== "popstate") {
      return;
    }

    if (source === "popstate" && ownedEntry) {
      const result = onBrowserBackRef.current?.(ownedEntry);

      if (result === "handled") {
        window.history.forward();
        return;
      }
    }

    ownedEntryRef.current = null;
    onOpenChangeRef.current(false);
  }, [activeEntry, enabled, instanceId, kind, name, open, revision, source]);

  return {
    active: Boolean(
      findOwnedEntry({
        entry: activeEntry,
        instanceId,
        kind,
        name,
      }),
    ),
    close: closeHistoryEntry,
    dismiss: dismissHistoryEntry,
    instanceId,
    onOpenChange: handleOpenChange,
  };
};
